// import global variables.
import {randomString, getQueryParameters} from '../utils/global';

class Tab {
    /**
     * タブ
     *
     * @constructor
     * @param {HTMLElement} root - ルートとなる要素
     * @param {Object} options - 設定の変更をする際のオブジェクト
     */
    constructor(root, options) {
        const config = {
            defaultCurrent: 0,
            className: {
                list: 'js-tab__list',
                listItem: 'js-tab__listItem',
                ctrl: 'js-tab__control',
                content: 'js-tab__content'
            },
            prefix: {
                id: 'tab-'
            }
        };

        if (!root) {
            return;
        }

        this.config = Object.assign(config, options);
        this.root = root;
        this.list = this.root.querySelector(`.${this.config.className.list}`);
        this.items = this.list.querySelectorAll(`.${this.config.className.listItem}`);
        this.ctrls = Array.from(this.list.querySelectorAll(`.${this.config.className.ctrl}`));
        this.isAnalyze = this.root.dataset.analyze === 'true';
        this.paramData = this.isAnalyze ? getQueryParameters() : {};
        this.contents = this.ctrls.map((element) => document.getElementById(element.hash.slice(1)));
        this.currentIndex = this.getCurrentIndex();
        this.currentId = this.contents[this.currentIndex].id;

        this.init();
        this.addEvent();
    }

    /**
     * init - 初期化
     * @return {Void}
     */
    init() {
        this.list.setAttribute('role', 'tablist');
        this.list.setAttribute('aria-activedescendant', this.contents[this.currentIndex].id);

        for (const item of this.items) {
            item.setAttribute('role', 'presentation');
        }

        this.ctrls.forEach((ctrl, idx) => {
            const content = this.contents[idx];
            const ctrlId = randomString(this.config.prefix.id);
            const isCurrent = idx === this.currentIndex;

            ctrl.id = ctrlId;
            ctrl.tabIndex = isCurrent ? 0 : -1;
            ctrl.setAttribute('role', 'tab');
            ctrl.setAttribute('aria-controls', this.contents[idx].id);
            ctrl.setAttribute('aria-selected', `${isCurrent}`);

            content.hidden = !isCurrent;
            content.tabIndex = 0;
            content.setAttribute('aria-labelledby', ctrlId);
            content.setAttribute('role', 'tabpanel');
        });
    }

    /**
     * getCurrentIndex - URLパラメータを解析してデフォルトで表示するタブのindex番号を取得する
     * @return {Number}
     */
    getCurrentIndex() {
        const paramStrings = this.paramData.hasOwnProperty('tab') ? this.paramData.tab.split(',') : [];
        let index = this.config.defaultCurrent;

        // タブに関するパラメータがない場合 または data-analyze="true"が指定されていない場合、パラメータ解析は行わない
        if (!paramStrings.length || !this.isAnalyze) {
            return index;
        }

        // パラメータ解析
        for (let i = 0; i < this.contents.length; i++) {
            // 一致するものがあればそのindexを返却
            if (paramStrings.includes(this.contents[i].id)) {
                index = i;
                break;
            }
        }

        return index;
    }

    /**
     * addEvent - イベントバインド
     */
    addEvent() {
        for (const ctrl of this.ctrls) {
            ctrl.addEventListener('click', (e) => {
                const oldCurrentId = this.currentId;

                e.preventDefault();

                this.currentIndex = [].indexOf.call(this.ctrls, e.currentTarget);
                this.currentId = this.contents[this.currentIndex].id;

                // パラメータ解析が有効な場合、パラメータを更新
                if (this.isAnalyze) {
                    this.updateParameters(oldCurrentId);
                }

                this.setCurrent();
            });
        }

        this.list.addEventListener('keydown', this.keydown.bind(this));
    }

    /**
     * setCurrent - タブを更新
     */
    setCurrent() {
        this.contents.forEach((content, idx) => {
            const ctrl = this.ctrls[idx];
            const isMatchIndex = idx === this.currentIndex;

            content.hidden = !isMatchIndex;
            ctrl.setAttribute('aria-selected', `${isMatchIndex}`);
            ctrl.tabIndex = isMatchIndex ? 0 : -1;
        });

        this.list.setAttribute('aria-activedescendant', this.currentId);
    }

    /**
     * updateParameters - パラメータを更新する
     * @param  {String} oldCurrentId 更新前のid名
     * @return {Void}
     */
    updateParameters(oldCurrentId) {
        const currentParam = window.location.search;
        let queryString = '';

        // パラメータ情報を最新化
        this.paramData = getQueryParameters(currentParam);

        // すでにタブに関するパラメータが存在する場合
        if (this.paramData.hasOwnProperty('tab')) {
            // 更新前のカレントIDがパラメータ上に存在する場合、IDを文字列置換
            if (this.paramData.tab.indexOf(oldCurrentId) !== -1) {
                queryString = `${currentParam.replace(oldCurrentId, this.currentId)}`;

            // 更新前のカレントIDが存在しない場合、末尾に追加
            } else {
                queryString = `${currentParam.replace(this.paramData.tab, `${this.paramData.tab},${this.currentId}`)}`;
            }

        // タブに関するパラメータが存在しない場合
        } else {
            // パラメータ自体がなければ '?' を付けて追加、存在していれば '&' を付けて追加
            queryString = `${currentParam === '' ? '?' : `${currentParam}&`}tab=${this.currentId}`;
        }

        window.history.replaceState(null, null, `${queryString}${window.location.hash}`);
    }

    /**
     * keydown - キーダウン時の処理
     * @param  {keydownEvent} event - イベントオブジェクト
     * @return {Void}
     */
    keydown(event) {
        const key = event.key.replace('Arrow', '').toLowerCase();

        // 左右矢印キー以外は何もしない
        if (key !== 'left' && key !== 'right') {
            return;
        }

        // カレントが一番初めのタブだった場合、最後のタブをカレントにする。それ以外はひとつ前をカレントに
        if (key === 'left') {
            this.currentIndex = this.currentIndex === 0 ? this.contents.length - 1 : this.currentIndex - 1;

        // カレントが一番最後のタブだった場合、最初のタブをカレントにする。それ以外はひとつ後をカレントに
        } else if (key === 'right') {
            this.currentIndex = this.currentIndex === (this.contents.length - 1) ? 0 : this.currentIndex + 1;
        }

        this.ctrls[this.currentIndex].focus();
        this.setCurrent();
    }
}

/**
 * デフォルト実行処理
 * @return {Void}
 */
export default () => {
    // 汎用タブ
    document.querySelectorAll('.js-tab').forEach((element) => new Tab(element));
};
